import { Hotspot, type HotspotDef } from "../../interaction/hotspots";
import { ACCESSIBLE_BAY, PREOP, MOTO_TRACK, DELIVERY_ZONE, MEETING_POINT, STREET } from "./layout";

/**
 * Hotspots de la sede: fichas educativas con etiqueta probatoria.
 * Posiciones en coordenadas del lote (el edificio está centrado en z=-15;
 * las coordenadas de habitaciones son locales al edificio → z - 15).
 */
export const SEDE_HOTSPOTS: readonly HotspotDef[] = [
  {
    id: "bahia-accesible",
    label: "Bahía accesible",
    position: [ACCESSIBLE_BAY.x, 1.2, ACCESSIBLE_BAY.z],
    description:
      "Bahía exclusiva para vehículos de personas con discapacidad y vehículos de examen registrados en el RUNT, con símbolo NTC 4139, franja de transferencia lateral de 1.2 m y ruta accesible pintada bahía → acceso.",
    tag: "NORMATIVO",
    source: "Anexo 60, num. 2.1 f) y 2.2.1; NTC 4139.",
  },
  {
    id: "recepcion",
    label: "Recepción y registro",
    position: [-15, 1.9, -6.5],
    description:
      "Punto de registro con equipos y kiosco de autoregistro (cámara, lector de huella, pad de firma). Escénico, sin captura real de datos. Nota de programa: el archivo y la zona administrativa se ubican en la segunda planta.",
    tag: "NORMATIVO",
    source: "Res. 8150/2026, Anexo Técnico.",
  },
  {
    id: "cubiculo-verificacion",
    label: "Cubículo de verificación",
    position: [9.7, 1.7, -7],
    description: "Cubículo privado para verificación documental y control de identidad del aspirante.",
    tag: "NORMATIVO",
    source: "Anexo 60, num. 1.1.1.",
  },
  {
    id: "puesto-teorico",
    label: "Puesto teórico",
    position: [-15.2, 1.7, -22],
    description:
      "Puesto individual de evaluación teórica: cubículos separados físicamente, mínimo 2 m² por aspirante, con equipo y silla.",
    tag: "NORMATIVO",
    source: "Anexo 60, num. 1.1.1 a).",
  },
  {
    id: "puesto-supervisor",
    label: "Puesto de supervisor",
    position: [-8, 1.9, -25],
    description: "Puesto elevado al fondo de la sala teórica con visión total de los 12 puestos de evaluación.",
    tag: "NORMATIVO",
    source: "Anexo 60, num. 1.4.5.",
  },
  {
    id: "camara-domo",
    label: "Cámara de videovigilancia",
    position: [-12, 2.6, -22],
    description: "Cámara domo de techo por cubículo (representación escénica; no captura video real).",
    tag: "SIMULADO",
    source: "Anexo 60, num. 1.1.1; Res. 8150/2026.",
  },
  {
    id: "ruta-evacuacion",
    label: "Ruta de evacuación",
    position: [-14, 1.4, -11],
    description:
      "Corredor accesible con salidas señalizadas (placas en altura y piso), iluminación de emergencia, extintores y botiquín escénicos, y recorrido de egreso norte hasta el punto de encuentro demarcado en el exterior.",
    tag: "NORMATIVO",
    source: "NSR-10 Título K; Dec. 1072/2015.",
  },
  {
    id: "vehiculo-preoperacional",
    label: "Vehículo en revisión preoperacional",
    position: [PREOP.x, 1.8, PREOP.z],
    description:
      "Estación techada de alistamiento diario (llantas, niveles, luces, frenos) con registro. La entrega del vehículo se hace en la zona demarcada junto a la línea de salida de la primera maniobra. Vehículos blancos con rótulo “PRUEBA” verde Pantone 3425 retroreflectivo y “CALE” en el capó.",
    tag: "NORMATIVO",
    source: "Anexo 60, num. 2.2.7, 2.2.1 y 2.3.1 Nota 8.",
  },
  {
    id: "cono-pista",
    label: "Cono de señalización",
    position: [MOTO_TRACK.x - MOTO_TRACK.w / 2 + 4, 1.3, MOTO_TRACK.z - 2.5],
    description:
      "Conos de 90 cm con banda reflectiva que marcan el circuito de destreza. Las franjas de seguridad de 1 m en los cuatro bordes están bordeadas por barreras plásticas enlazadas rojo/blanco.",
    tag: "NORMATIVO",
    source: "Anexo 60, num. 2.3.1 Nota 6 y 2.3.1.1 Notas 11–12.",
  },
  {
    id: "via-publica",
    label: "Conexión a vía pública",
    position: [44, 1.4, STREET.z - 3],
    description:
      "Portón vehicular en el cerramiento sur (z=44): la vía pública queda fuera del lote. Las maniobras en vía se realizan solo en jornada diurna (6:00–18:00).",
    tag: "NORMATIVO",
    source: "Anexo 60, num. 2.1.1.",
  },
  {
    id: "casilleros",
    label: "Casilleros",
    position: [-19.5, 1.6, -12.4],
    description:
      "Banco de 8 casilleros junto a recepción/verificación para el almacenamiento de artículos personales no permitidos en la sala de examen.",
    tag: "NORMATIVO",
    source: "Anexo 60, num. 1.1.1 a) (programa de espacios).",
  },
  {
    id: "zona-entrega",
    label: "Zona de entrega del vehículo",
    position: [DELIVERY_ZONE.x, 1.2, DELIVERY_ZONE.z],
    description:
      "Zona demarcada de entrega del vehículo de examen, junto al punto de inicio de la primera maniobra de la pista de automóviles.",
    tag: "NORMATIVO",
    source: "Anexo 60, num. 2.3.1, Nota 8.",
  },
  {
    id: "punto-encuentro",
    label: "Punto de encuentro",
    position: [MEETING_POINT.x, 1.6, MEETING_POINT.z],
    description:
      "Punto de encuentro señalizado al final de la ruta de egreso norte: demarcación circular verde y placa, parte del plan de emergencias.",
    tag: "NORMATIVO",
    source: "Dec. 1072/2015 (SG-SST); NSR-10 Título K.",
  },
];

export function SedeHotspots() {
  return (
    <group name="hotspots">
      {SEDE_HOTSPOTS.map((def) => (
        <Hotspot key={def.id} def={def} />
      ))}
    </group>
  );
}
